import React from "react";
import { AlertTriangle, AlertCircle, X } from "lucide-react";

/**
 * Delete Confirmation Modal Component
 * Reusable modal for confirming destructive actions
 */
const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Delete Item",
  itemName,
  description,
  warningMessage,
  confirmButtonText = "Delete",
  isLoading = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={isLoading ? undefined : onClose}
      />
      <div className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl duration-200 animate-in fade-in-0 zoom-in-95">
        {/* Header */}
        <div className="flex items-start mb-6 space-x-4">
          <div className="flex-shrink-0 p-3 bg-red-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="mb-1 text-lg font-bold text-gray-900">
              {title}
            </h3>
            <p className="text-sm text-gray-600">
              {description || (
                <>
                  Are you sure you want to delete{" "}
                  <span className="font-semibold text-gray-900">
                    {itemName}
                  </span>
                  ? This action cannot be undone.
                </>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-shrink-0 p-1 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Warning Message */}
        {warningMessage && (
          <div className="p-4 mb-6 bg-red-50 rounded-xl border border-red-100">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="mb-1 font-medium">Warning:</p>
                <p>{warningMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex flex-1 justify-center items-center px-4 py-3 font-semibold text-white bg-red-600 rounded-xl transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="mr-2 w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                Deleting...
              </>
            ) : (
              confirmButtonText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;

